import './style.scss';
import { SystemClock } from '../../services/system-clock.service';
import useFetch, { Status } from '../../../../hooks/fetch.hook';
import type ExerciseApiModel from '../../models/exercise.api-model';
import Spinner from '../../../Shared/Spinner';
import type { ILeg } from '../../../../models/pa-egna-ben/leg.model';
import type LegResultApiModel from '../../models/leg-result.api-model';
import LegStep from './LegStep';
import { Role } from '../../../../models/pa-egna-ben/role.enum';
import { StepState } from './LegStep/step-state.enum';

function LegStepIndicator({
  participationId,
  competitionLegs,
  legResults,
  viewerRole = Role.anonymous,
}: {
  participationId: number;
  competitionLegs: ILeg[];
  legResults: LegResultApiModel[];
  viewerRole?: Role;
}) {
  const now = SystemClock.now();
  const [{ status: exercisesStatus, data: exercisesData }] = useFetch<ExerciseApiModel[]>('/api/paegnaben/participation-overview/exercises');

  function getStepState(leg: ILeg | undefined, legResult: LegResultApiModel | undefined): StepState {
    if (!leg) {
      return StepState.Future;
    }
    if (leg.endTime < now || legResult) {
      return StepState.Done;
    }
    if (leg.startTime < now && now < leg.endTime) {
      return StepState.Active;
    }
    return StepState.Future;
  }

  function getCompetitionLegResult(legStep: number): LegResultApiModel | undefined {
    return legResults.find((lr) => lr.legId === competitionLegs.find((cl) => cl.number === legStep)?.id);
  }

  function getLegByNumber(legNumber: number): ILeg | undefined {
    return competitionLegs.find((cl) => cl.number === legNumber);
  }

  return (
    <div className="leg-indicator">
      {exercisesStatus === Status.loading && <Spinner />}
      <div>
        <div className="leg-indicator-bars">
          <LegStep
            stepState={getStepState(getLegByNumber(1), getCompetitionLegResult(1))}
            viewerRole={viewerRole}
            participationId={participationId}
            step={1}
            legResult={getCompetitionLegResult(1)!}
            exercises={exercisesData}
            competitionLegs={competitionLegs}
          />
        </div>
        <div className="leg-indicator-bars">
          <LegStep
            stepState={getStepState(getLegByNumber(2), getCompetitionLegResult(2))}
            viewerRole={viewerRole}
            participationId={participationId}
            step={2}
            legResult={getCompetitionLegResult(2)!}
            exercises={exercisesData}
            competitionLegs={competitionLegs}
          />
        </div>
        <div className="leg-indicator-bars">
          <LegStep
            stepState={getStepState(getLegByNumber(3), getCompetitionLegResult(3))}
            viewerRole={viewerRole}
            participationId={participationId}
            step={3}
            legResult={getCompetitionLegResult(3)!}
            exercises={exercisesData}
            competitionLegs={competitionLegs}
          />
        </div>
        <div className="leg-indicator-bars">
          <LegStep
            stepState={getStepState(getLegByNumber(4), getCompetitionLegResult(4))}
            viewerRole={viewerRole}
            participationId={participationId}
            step={4}
            legResult={getCompetitionLegResult(4)!}
            exercises={exercisesData}
            competitionLegs={competitionLegs}
          />
        </div>
        <div className="leg-indicator-bars">
          <LegStep
            stepState={getStepState(getLegByNumber(5), getCompetitionLegResult(5))}
            viewerRole={viewerRole}
            participationId={participationId}
            step={5}
            legResult={getCompetitionLegResult(5)!}
            exercises={exercisesData}
            competitionLegs={competitionLegs}
          />
        </div>
      </div>
    </div>
  );
}

export default LegStepIndicator;
